// src/components/DocumentExplorer.js
// File Explorer-style document management with virtual folder structure

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import 'animate.css';

const DocumentExplorer = ({ authAxios, caseId, onToast }) => {
  const [folders, setFolders] = useState([]);
  const [serverFolders, setServerFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showMetadataPanel, setShowMetadataPanel] = useState(false);
  const [expandedVersions, setExpandedVersions] = useState({});

  // Fetch folders for the case
  const fetchFolders = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const resp = await authAxios.get(`/document-folders/?case=${caseId}`);
      const data = resp.data?.results || resp.data || [];
      const server = Array.isArray(data) ? data : [];
      setServerFolders(server);
      // We'll merge server folders with virtual type folders after documents are loaded
      setFolders(server);
      // Auto-select first folder if none selected
      if (!selectedFolder && server.length > 0) {
        setSelectedFolder(server[0]);
      }
    } catch (err) {
      console.error('Failed to fetch folders', err);
      onToast?.('Failed to load folders', 'error');
    } finally {
      setLoading(false);
    }
  }, [authAxios, caseId, selectedFolder, onToast]);

  // Fetch documents for selected folder
  const fetchDocuments = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const params = { case: caseId };
      // If a virtual folder (by document type) is selected, filter by document_type
      if (selectedFolder && selectedFolder.isVirtual && selectedFolder.docType) {
        params.document_type = selectedFolder.docType;
      } else if (selectedFolder && !selectedFolder.isVirtual) {
        params.folder = selectedFolder.id;
      }
      const resp = await authAxios.get('/documents/', { params });
      const data = resp.data?.results || resp.data || [];
      const docs = Array.isArray(data) ? data : [];
      setDocuments(docs);

      // To build a stable set of virtual folders we must consider ALL documents
      // in the case (not just the currently-selected folder's documents). Fetch
      // all documents for the case and derive the document types from that set.
      let allDocs = docs;
      try {
        const allResp = await authAxios.get('/documents/', { params: { case: caseId } });
        const allData = allResp.data?.results || allResp.data || [];
        allDocs = Array.isArray(allData) ? allData : [];
      } catch (e) {
        // If fetching all documents fails, fall back to the filtered set
        console.warn('Failed to fetch full document list for folder computation', e);
      }

      // Build virtual folders from document_type values using the full document set
      const typesSet = new Set(allDocs.map(d => (d.document_type || 'Other')));
      const typeFolders = Array.from(typesSet).map((t, i) => ({ id: `type-${t}`, name: t, docType: t, isVirtual: true }));
      // Add explicit "All Types" virtual folder at the front
      const allTypesFolder = { id: 'type-All', name: 'All Types', docType: null, isVirtual: true };
      if (!typeFolders.find(tf => tf.id === allTypesFolder.id)) {
        typeFolders.unshift(allTypesFolder);
      }
      // Merge serverFolders and typeFolders, avoiding duplicate names
      const merged = [...(serverFolders || [])];
      // Ensure All Types virtual folder exists in merged list as first virtual entry
      if (!merged.find(m => m.id === allTypesFolder.id)) merged.unshift(allTypesFolder);
      typeFolders.forEach(tf => {
        if (!merged.find(m => String(m.name) === String(tf.name))) merged.push(tf);
      });
      setFolders(merged);

      // Auto-select a folder if none selected
      if (!selectedFolder && merged.length > 0) setSelectedFolder(merged[0]);

    } catch (err) {
      console.error('Failed to fetch documents', err);
      onToast?.('Failed to load documents', 'error');
    } finally {
      setLoading(false);
    }
  }, [authAxios, caseId, selectedFolder, onToast, serverFolders]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders, caseId]);

  useEffect(() => {
    if (caseId) {
      fetchDocuments();
    }
  }, [fetchDocuments, caseId, selectedFolder]);

  // Handle file upload
  const handleFileUpload = useCallback(async (files) => {
    if (!files || files.length === 0 || !caseId) return;
    if (!selectedFolder) {
      onToast?.('Please select a folder (or All Types) before uploading.', 'error');
      return;
    }
    setUploading(true);
    try {
      onToast?.(`Uploading ${files.length} file(s)...`, 'info');
      // Upload files sequentially
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('case', caseId);
        if (!selectedFolder.isVirtual) {
          formData.append('folder', selectedFolder.id);
        } else {
          const docType = selectedFolder.docType || 'Other';
          formData.append('document_type', docType);
        }
        formData.append('title', file.name);

        await authAxios.post('/documents/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      onToast?.('Files uploaded successfully', 'success');
      fetchDocuments();
    } catch (err) {
      console.error('Upload failed', err);
      onToast?.('Upload failed', 'error');
    } finally {
      setUploading(false);
      setDragging(false);
    }
  }, [authAxios, caseId, selectedFolder, fetchDocuments, onToast]);

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };

  // Move document to folder (future enhancement)
  // eslint-disable-next-line no-unused-vars
  const moveDocumentToFolder = useCallback(async (docId, folderId) => {
    try {
      await authAxios.patch(`/documents/${docId}/`, { folder: folderId });
      onToast?.('Document moved', 'success');
      fetchDocuments();
    } catch (err) {
      console.error('Failed to move document', err);
      onToast?.('Failed to move document', 'error');
    }
  }, [authAxios, fetchDocuments, onToast]);

  // Change a document's document_type (used when dragging to type-folders)
  const changeDocumentType = useCallback(async (docId, docType) => {
    try {
      await authAxios.patch(`/documents/${docId}/`, { document_type: docType });
      onToast?.('Document type updated', 'success');
      fetchDocuments();
    } catch (err) {
      console.error('Failed to change document type', err);
      onToast?.('Failed to change document type', 'error');
    }
  }, [authAxios, fetchDocuments, onToast]);

  // Get document version count
  const getVersionCount = (doc) => {
    return doc.versions?.length || 0;
  };

  // Get latest version number
  const getLatestVersionNumber = (doc) => {
    if (!doc.versions || doc.versions.length === 0) return 1;
    return Math.max(...doc.versions.map(v => v.version_number || 1));
  };

  // Toggle version expansion
  const toggleVersions = (docId) => {
    setExpandedVersions(prev => ({
      ...prev,
      [docId]: !prev[docId]
    }));
  };

  // Handle verify document
  const handleVerifyDocument = async (documentId) => {
    try {
      await authAxios.post(`documents/${documentId}/verify/`);
      onToast?.('Document verified successfully!', 'success');
      // Refresh documents
      fetchDocuments();
      // Update selected doc if it's the one being verified
      if (selectedDoc?.id === documentId) {
        setSelectedDoc({ ...selectedDoc, is_verified: true });
      }
    } catch (error) {
      console.error('Error verifying document:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to verify document.';
      onToast?.(errorMessage, 'error');
    }
  };

  // Group documents by showing only latest version unless expanded
  const displayDocuments = useMemo(() => {
    return documents.map(doc => ({
      ...doc,
      showVersions: expandedVersions[doc.id] || false,
      versionCount: getVersionCount(doc),
      latestVersion: getLatestVersionNumber(doc)
    }));
  }, [documents, expandedVersions]);

  // Render folder tree
  const renderFolderTree = () => (
    <div className="h-full bg-gray-50 border-r border-gray-200">
      <div className="p-3 border-b border-gray-200 bg-white">
        <h3 className="font-semibold text-gray-700 flex items-center gap-2">
          <i className="bi bi-folder2-open"></i>
          Folders
        </h3>
      </div>
      <div className="p-2 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
        {loading && folders.length === 0 ? (
          <div className="text-center text-gray-500 py-4">Loading...</div>
        ) : folders.length === 0 ? (
          <div className="text-center text-gray-500 py-4 text-sm">No folders</div>
        ) : (
          folders.map(folder => (
            <div
              key={folder.id}
              onClick={() => setSelectedFolder(folder)}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => {
                e.preventDefault();
                const docId = e.dataTransfer.getData('text/plain');
                if (!docId) return;
                // If virtual type folder, change document_type, else move to server folder
                if (folder.isVirtual) {
                  const targetType = folder.docType || 'Other';
                  changeDocumentType(docId, targetType);
                } else {
                  moveDocumentToFolder(docId, folder.id);
                }
              }}
              className={`p-2 rounded cursor-pointer transition-colors flex items-center gap-2 ${
                selectedFolder?.id === folder.id
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <i className="bi bi-folder"></i>
              <span className="text-sm truncate">{folder.name}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // Render document grid
  const renderDocumentGrid = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
      {displayDocuments.map(doc => (
        <div key={doc.id} className="relative group">
          <div
            draggable
            onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(doc.id)); }}
            onClick={() => {
              setSelectedDoc(doc);
              setShowMetadataPanel(true);
            }}
            className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-lg transition-all hover:border-blue-300"
          >
            <div className="flex flex-col items-center gap-2">
              <div className="text-4xl text-blue-500">
                {doc.file?.endsWith('.pdf') ? (
                  <i className="bi bi-file-pdf"></i>
                ) : doc.file?.endsWith('.docx') || doc.file?.endsWith('.doc') ? (
                  <i className="bi bi-file-word"></i>
                ) : (
                  <i className="bi bi-file-earmark"></i>
                )}
              </div>
              <div className="text-sm font-medium text-gray-700 text-center truncate w-full">
                {doc.title}
              </div>
              {doc.versionCount > 0 && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleVersions(doc.id);
                  }}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full cursor-pointer hover:bg-blue-200"
                >
                  <i className="bi bi-clock-history"></i>
                  v{doc.latestVersion}
                  {doc.versionCount > 1 && ` (+${doc.versionCount - 1})`}
                </div>
              )}
            </div>
          </div>
          {/* Version history expanded */}
          {doc.showVersions && doc.versions && doc.versions.length > 0 && (
            <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-2">
              <div className="text-xs font-semibold text-gray-600 mb-2">Version History</div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {doc.versions.map(ver => (
                  <div key={ver.id} className="flex items-center justify-between text-xs p-1 hover:bg-gray-50 rounded">
                    <span className="font-medium">v{ver.version_number || 1}</span>
                    <span className="text-gray-500">{new Date(ver.uploaded_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // Render document list
  const renderDocumentList = () => (
    <div className="p-4">
      <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Name</th>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Type</th>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Version</th>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Uploaded</th>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody>
          {displayDocuments.map(doc => (
            <React.Fragment key={doc.id}>
              <tr className="border-t hover:bg-gray-50">
                <td className="px-4 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <i className="bi bi-file-earmark text-blue-500"></i>
                    <span className="font-medium">{doc.title}</span>
                  </div>
                </td>
                <td className="px-4 py-2 text-sm text-gray-600">{doc.document_type || 'Other'}</td>
                <td className="px-4 py-2 text-sm">
                  {doc.versionCount > 0 && (
                    <button
                      onClick={() => toggleVersions(doc.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full hover:bg-blue-200"
                    >
                      <i className="bi bi-clock-history"></i>
                      v{doc.latestVersion}
                      {doc.versionCount > 1 && ` (+${doc.versionCount - 1})`}
                    </button>
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-gray-600">
                  {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm">
                  <button
                    onClick={() => {
                      setSelectedDoc(doc);
                      setShowMetadataPanel(true);
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <i className="bi bi-info-circle"></i>
                  </button>
                </td>
              </tr>
              {/* Version rows */}
              {doc.showVersions && doc.versions && doc.versions.map(ver => (
                <tr key={`${doc.id}-v${ver.id}`} className="border-t bg-blue-50">
                  <td className="px-4 py-2 text-sm pl-12">
                    <span className="text-gray-600">└ Version {ver.version_number || 1}</span>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">-</td>
                  <td className="px-4 py-2 text-sm text-gray-600">-</td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {new Date(ver.uploaded_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-sm">
                    {ver.file && (
                      <a
                        href={ver.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <i className="bi bi-download"></i>
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Render metadata panel (right sidebar)
  const renderMetadataPanel = () => {
    if (!showMetadataPanel || !selectedDoc) return null;

    return (
      <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-700">Document Details</h3>
          <button
            onClick={() => setShowMetadataPanel(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        <div className="p-4 space-y-4 flex-1">
          {/* Preview placeholder */}
          <div className="bg-gray-100 rounded-lg p-4 text-center">
            <i className="bi bi-file-earmark-text text-4xl text-gray-400"></i>
            <div className="text-sm text-gray-600 mt-2">Preview unavailable</div>
          </div>

          {/* Metadata */}
          <div className="space-y-2">
            <div>
              <div className="text-xs text-gray-500 uppercase">Title</div>
              <div className="text-sm font-medium">{selectedDoc.title}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase">Type</div>
              <div className="text-sm">{selectedDoc.document_type || 'Other'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase">Uploaded</div>
              <div className="text-sm">
                {selectedDoc.uploaded_at
                  ? new Date(selectedDoc.uploaded_at).toLocaleString()
                  : 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase">Uploaded By</div>
              <div className="text-sm">{selectedDoc.uploaded_by_name || 'Unknown'}</div>
            </div>
            {selectedDoc.description && (
              <div>
                <div className="text-xs text-gray-500 uppercase">Description</div>
                <div className="text-sm">{selectedDoc.description}</div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="pt-4 border-t border-gray-200 space-y-2">
            {selectedDoc.file && (
              <a
                href={selectedDoc.file}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                <i className="bi bi-download"></i>
                Download
              </a>
            )}
            {selectedDoc.is_verified ? (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <i className="bi bi-check-circle-fill"></i>
                Verified
              </div>
            ) : (
              <button 
                onClick={() => handleVerifyDocument(selectedDoc.id)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-green-600 text-green-600 rounded hover:bg-green-50 transition-colors"
              >
                <i className="bi bi-shield-check"></i>
                Verify Document
              </button>
            )}
          </div>

          {/* Versions */}
          {selectedDoc.versions && selectedDoc.versions.length > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <div className="text-sm font-semibold text-gray-700 mb-2">
                Version History ({selectedDoc.versions.length})
              </div>
              <div className="space-y-2">
                {selectedDoc.versions.map(ver => (
                  <div
                    key={ver.id}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                  >
                    <div>
                      <div className="text-sm font-medium">Version {ver.version_number || 1}</div>
                      <div className="text-xs text-gray-500">
                        {new Date(ver.uploaded_at).toLocaleDateString()}
                      </div>
                    </div>
                    {ver.file && (
                      <a
                        href={ver.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <i className="bi bi-download"></i>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!caseId) {
    return (
      <div className="p-8 text-center text-gray-500">
        Please select a case to view documents
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-gray-800">Document Explorer</h2>
          {selectedFolder && (
            <span className="text-sm text-gray-600">
              <i className="bi bi-folder"></i> {selectedFolder.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex border border-gray-300 rounded">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1 text-sm ${
                viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="bi bi-grid-3x3-gap"></i>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 text-sm border-l border-gray-300 ${
                viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="bi bi-list-ul"></i>
            </button>
          </div>
          {/* Upload button */}
          <label className="px-4 py-2 bg-blue-600 text-white rounded cursor-pointer hover:bg-blue-700 transition-colors">
            <i className="bi bi-upload mr-2"></i>
            Upload
            <input
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleFileUpload(e.target.files)}
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      {/* Main content - 3 pane layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Folder tree */}
        <div className="w-64 flex-shrink-0">{renderFolderTree()}</div>

        {/* Center: Documents */}
        <div
          className="flex-1 overflow-y-auto relative"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {dragging && (
            <div className="absolute inset-0 bg-blue-100 bg-opacity-50 border-4 border-dashed border-blue-400 flex items-center justify-center z-10">
              <div className="text-center">
                <i className="bi bi-cloud-upload text-6xl text-blue-600"></i>
                <div className="text-xl font-semibold text-blue-700 mt-2">Drop files here</div>
              </div>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">Loading documents...</div>
            </div>
          ) : documents.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <i className="bi bi-folder2-open text-6xl mb-4"></i>
                <div>No documents in this folder</div>
                <div className="text-sm mt-2">Drop files here or click Upload</div>
              </div>
            </div>
          ) : viewMode === 'grid' ? (
            renderDocumentGrid()
          ) : (
            renderDocumentList()
          )}
        </div>

        {/* Right: Metadata panel */}
        {renderMetadataPanel()}
      </div>
    </div>
  );
};

export default DocumentExplorer;
